import {
  IFrameStyleOptions,
  IVerifyIQ,
  RenderByApplicationIdParams,
  RenderHitlParams,
} from 'types/SDK.interface';
import { AuthTypes } from 'types/auth-types.enum';
import {
  EventCallback,
  DocumentRequestCallback,
} from 'types/callback.interface';
import { EventsEnum } from 'types/events.enum';
import { IpcMessage } from 'types/ipc.interface';
import Renderer from './renderer/renderer';
import {
  ApplicantTypes,
  StipulationTypes,
  EnvTypes,
} from 'constants/api.constants';
import invariant from './utils/invariant';
import { DocumentModalOptions } from 'types/document-modal-options.enum';
import { PortalRoutesEmun } from 'types/routes.enum';

interface SDKOptions {
  partnerName: string;
  url?: string;
  authType?: AuthTypes;
  actionCallbackWebhookUrl?: string;
  environment: EnvTypes;
  onDocumentRequestedViaSms?: DocumentRequestCallback;
  onLoad?: EventCallback<any>;
}

class VerifyIQ implements IVerifyIQ {
  public static Env = EnvTypes;
  public static ApplicantTypes = ApplicantTypes;
  public static StipulationTypes = StipulationTypes;
  public static DocumentModalOptions = DocumentModalOptions;

  /**
   * Renderer instance
   */
  private renderer: Renderer;

  private ready = false;

  constructor(options: SDKOptions) {
    this.renderer = new Renderer();
    if (options.environment) {
      const postfix =
        options.environment === VerifyIQ.Env.Production ? '' : '-infra';
      this.renderer.setUrl(
        options.url
          ? options.url
          : `https://${options.partnerName}.verifyiq.${options.environment}.informediq${postfix}.com`,
      );
      this.ready = true;
    }
    console.log('setting auth');

    const { authType } = options;
    authType ? this.setAuth(authType) : this.setAuth(AuthTypes.Popup); //can we change it to this -> setAuth(authType?? AuthType.Popup)
    this.onLoad(options.onLoad);
    this.onDocumentRequestedViaSms(options.onDocumentRequestedViaSms);
    this.registerAfterLoadActions(options.actionCallbackWebhookUrl);
  }

  /**
   * Enable/Disable logging
   * @param isEnabled {Boolean}
   */
  public enableLogging(isEnabled: boolean) {
    this.renderer.enableLogging(isEnabled);
    return this;
  }

  /**
   * Transfers data to the frame when it's loaded
   * @param actionWebhookUrl {String}
   */
  private registerAfterLoadActions(actionWebhookUrl?: string) {
    if (!actionWebhookUrl) {
      return;
    }

    this.onLoad(() => {
      const command = new IpcMessage(
        EventsEnum.ActionWebhookUrlInitialize,
        actionWebhookUrl,
      );
      this.renderer.exec(command);
    });
  }
  /**
   * Defines SAML Login type
   * @param authType {AuthTypes}
   */
  private setAuth(authType: AuthTypes) {
    this.renderer.setAuth(authType);
    return this;
  }

  /**
   * Register callback for iframe onLoad event
   * @param callback {VerificationActionCallback}
   */
  private onLoad(callback?: EventCallback<any>) {
    if (!callback) return;

    return this.renderer.on(EventsEnum.Loaded, callback);
  }

  /**
   * Register callback for RequestIQ SMS Send event
   * @param callback {DocumentRequestCallback}
   */
  private onDocumentRequestedViaSms(callback?: DocumentRequestCallback) {
    if (!callback) return;

    this.renderer.on(EventsEnum.DocumentRequestedViaSMS, callback);
  }
  /**
   * Render SDK
   * @param htmlElement {HTMLElement}
   * @param styleOptions
   */
  private render(
    htmlElement: HTMLElement,
    styleOptions?: IFrameStyleOptions,
  ): void {
    invariant(this.ready, 'Environment is required');
    this.renderer.setRoot(htmlElement);
    this.renderer.render(styleOptions);
  }

  /**
   * Render application by id
   * @param htmlElement {HTMLElement}
   * @param applicationId {String}
   * @param applicant
   * @param stipulation
   * @param documentModalOptions
   */
  public renderApplicationId({
    htmlElement,
    applicationId,
    applicant = VerifyIQ.ApplicantTypes.PrimaryApplicant,
    stipulation = '',
    documentModalOptions = DocumentModalOptions.UploadDocuments,
  }: RenderByApplicationIdParams) {
    this.renderer.applicationId = applicationId;
    this.renderer.applicant = applicant;
    this.renderer.stipulation = stipulation;
    this.renderer.documentModalOption = documentModalOptions;
    this.renderer.portalRoute = PortalRoutesEmun.applications;

    this.render(htmlElement);
  }

  public renderRequestIq({
    htmlElement,
    applicationId,
    frameHeight,
    frameWidth,
  }: RenderByApplicationIdParams): void {
    this.renderer.applicationId = applicationId;
    this.renderer.portalRoute = PortalRoutesEmun.requestDocs;

    this.render(htmlElement, {
      height: frameHeight ?? '630px',
      width: frameWidth ?? '70vw',
    });
  }

  public renderHitl({
    htmlElement,
    applicationId,
    documentId,
    frameHeight,
    frameWidth,
  }: RenderHitlParams): void {
    this.renderer.applicationId = applicationId;
    this.renderer.portalRoute = PortalRoutesEmun.hitl;
    this.renderer.setQueryParam('documentId', documentId);

    this.render(htmlElement, {
      height: frameHeight ?? '630px',
      width: frameWidth ?? '70vw',
    });
  }
}

module.exports = VerifyIQ;
